import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import ActionStore from 'stores/action/ActionStore'
import ConfigStore from 'stores/config/ConfigStore'
import TelemetryStore from 'stores/telemetry/TelemetryStore'

import Button from 'components/button/Button'
import Mousetrap from 'components/mousetrap/Mousetrap'
import ReadonlyDialog from './ReadonlyDialog'

import styles from './pendingActions.scss'

interface IProps {
  className?: string
  onSuccess?: () => void
  onFailure?: () => void
}

interface IState {
  isReadonlyDialogOpen: boolean
}

class PendingActions extends React.PureComponent<IProps, IState> {
  state: IState = {
    isReadonlyDialogOpen: false,
  }

  handleDiscard = () => {
    const { onSuccess } = this.props

    ActionStore.discard()
    onSuccess?.()

    TelemetryStore.send({
      command: 'action_discard',
      commandDetails: { pending_action_count: ActionStore.actions.length },
    })
  }

  handleCommit = async () => {
    const { onSuccess, onFailure } = this.props

    if (ConfigStore.readonly) {
      return this.handleToggleReadonlyDialog()
    }

    try {
      await ActionStore.commit()
      onSuccess?.()
    } catch (e) {
      onFailure?.()
    }

    TelemetryStore.send({
      command: 'action_commit',
      commandDetails: { pending_action_count: ActionStore.actions.length },
    })
  }

  handleToggleReadonlyDialog = () => {
    this.setState(state => ({
      isReadonlyDialogOpen: !state.isReadonlyDialogOpen,
    }))
  }

  render() {
    const { isReadonlyDialogOpen } = this.state

    const allActionsCount = ActionStore.actions.filter(a => {
      if (a.type !== 'create') {
        return true
      }
      // If this is a create action, do not count any other (update) actions.
      // This prevents the count from being 2 when you only pressed `Add Record`
      return !ActionStore.actions.find(aa => aa.recordId === a.recordId)
    }).length
    const invalidCount = ActionStore.invalidActions.length

    if (allActionsCount === 0) {
      return null
    }

    return (
      <>
        <div className={styles.separator} />
        <div
          data-cy="pending-actions"
          className={cn(styles.container, this.props.className)}
        >
          <Button
            data-cy="commit-actions"
            green
            className={cn(styles.action, styles.confirmBtn)}
            disabled={invalidCount > 0 || ActionStore.committing}
            onClick={this.handleCommit}
          >
            {ActionStore.committing
              ? 'Saving Changes'
              : `Save ${allActionsCount} change${
                  allActionsCount > 1 ? 's' : ''
                }`}
          </Button>
          <Button
            data-cy="discard-actions"
            ghost
            disabled={ActionStore.committing}
            className={cn(styles.action, styles.discardBtn)}
            onClick={this.handleDiscard}
          >
            Discard changes
          </Button>

          {isReadonlyDialogOpen && (
            <ReadonlyDialog onClose={this.handleToggleReadonlyDialog} />
          )}

          <Mousetrap keys="mod+s" onMatch={() => ActionStore.commit()} />
        </div>
      </>
    )
  }
}

export default observer(PendingActions)
